<template>
    <div ref="elementRef" class="flex flex-col gap-4 p-6">
        <vx-alert
            :visible="Boolean(error)"
            @update:visible="error = ''"
            color="danger"
        >
            {{ error }}
        </vx-alert>

        <div v-if="!model.id">
            <vx-label>Phone Number</vx-label>
            <vx-selectable ref="phoneNumberRef"
                size="lg"
                autofocus
                v-model="model.contact_id"
                :loading="loadingContacts"
                :reduce="option => option.id"
                label="label"
                :options="contactList"
                @search="runSearch"
                relativeDropdown
                :error="displayFirstError(validate.contact_id.$errors)"
                placeholder="Enter phone number">
            </vx-selectable>
        </div>

        <div>
            <vx-label v-if="!model.id">Message</vx-label>
            <message-builder ref="messageBuilder"
                v-model="model.body"
                v-model:mediaUrl="model.media_url"
                v-model:mediaContentType="model.media_content_type"
                @selected:snippet="selectedSnippetBody = $event.body"
                :error="displayFirstError(validate.body.$errors) || displayFirstError(validate.send_at.$errors)"
                preview></message-builder>
        </div>
        <div>
            <vx-label>
                Schedule on
                <template v-slot:description>
                    Times shown in local time-zone ({{ timezone }}).
                </template>
            </vx-label>
            <div class="flex justify-center my-4">
                <vx-date-picker
                    :key="minDateKey"
                    v-model="model.send_at"
                    mode="datetime"
                    :timezone="getClientTz()"
                    :minDate="minDate"
                    is-required></vx-date-picker>
            </div>
        </div>
    </div>
</template>

<script setup>
import { isLargeTeam } from '~/utils/team';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { VxAlert, VxDatePicker, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { contactsClient } from '~/services';
import { computed, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { useMessageBuilderInModal } from '~/composables/useMessageBuilderInModal';
import debounce from '~/utils/debounce';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { formatPhoneNumber } from '~/components/filters';
import { displayFirstError } from '~/utils/validation';
import { formatTz, getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { useInterval } from '@vueuse/core';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

const teamId = Spark.state.currentTeam.id;

const model = defineModel({
    type: Object,
    required: true,
});

const error = defineModel('error', {
    type: String,
    default: '',
});

const validate = useVuelidate(
    {
        body: {
            required: required,
        },
        send_at: {
            required: required,
            minDate: helpers.withMessage('The scheduled date cannot be in the past.', (value) => dayjs(value).tz(getClientTz()).isAfter(dayjs().tz(getClientTz()))),
            maxDate: helpers.withMessage('The scheduled date cannot be greater than 90 days.', (value) => dayjs(value).tz(getClientTz()).isBefore(dayjs().tz(getClientTz()).add(90, 'days'))),
        },
        contact_id: {
            required: required,
        },
    },
    model,
);

const emit = defineEmits(['selected:snippet']);

const timezone = formatTz(dayjs().tz(getClientTz()).format('z'));
const toasted = useToasted();

const { stacked, preventClosing } = useMessageBuilderInModal();
const phoneNumberRef = ref();
const loadingContacts = ref(false);
const contactList = ref([]);
const selectedSnippetBody = ref('');

const minDate = computed(() => dayjs().tz(getClientTz()).add(15, 'minutes').toDate());
const minDateKey = useInterval(60000);

const contactLabel = (contact) => {
    const name = ((contact.first_name || '') + ' ' + (contact.last_name || '')).trim();

    if (name) {
        return `${name} - ${formatPhoneNumber(contact.phone)}`;
    }

    return formatPhoneNumber(contact.phone);
};

const runSearch = debounce(async (search) => {
    const params = {
        per_page: 5,
    };

    if (isLargeTeam(teamId)) {
        if (search.includes('@')) {
            params['filter[email]'] = search.trim();
        } else {
            params['filter[phone]'] = search.trim();
        }
    } else {
        params['filter[search]'] = search.trim();
    }

    try {
        loadingContacts.value = true;
        const response = await contactsClient.searchContacts(teamId, params);

        contactList.value = (response?.data?.data || []).map((contact) => {
            return {
                id: contact.id,
                label: contactLabel(contact),
            };
        });
    } catch (error) {
        if (error?.response?.status !== HTTP_NOT_FOUND) {
            console.error(error);
            toasted.global.platform_error();
        }
    } finally {
        loadingContacts.value = false;
    }
}, 300);

watch(() => selectedSnippetBody.value, (newValue) => {
    if (newValue) {
        emit('selected:snippet', { body: newValue });
    }
});

const elementRef = ref();

const scrollToTop = () => {
    elementRef.value?.scrollTo({ top: 0, behavior: 'smooth' });
}

defineExpose({
    scrollToTop,
    stacked,
    preventClosing,
})
</script>
